import { useState, useRef, useEffect } from "react";

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_ADMIN_API_URL || "https://ampsweb.saassrs.com";

const DEFAULT_COUNTRIES = [
  { value: "all", label: "All Countries" }
];

export default function CountrySelector({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [countries, setCountries] = useState(DEFAULT_COUNTRIES);
  const [filteredCountries, setFilteredCountries] = useState(DEFAULT_COUNTRIES);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/countries`);
        const data = await response.json();
        
        if (data.success && data.countries) {
          const allCountries = [
            { value: "all", label: "All Countries" },
            ...data.countries.map(c => ({
              value: c.value || c.id || c.code,
              label: c.label || c.name
            }))
          ];
          setCountries(allCountries);
        } else {
          console.error("Failed to fetch countries:", data);
          setCountries(DEFAULT_COUNTRIES);
        }
      } catch (err) {
        console.error("Error fetching countries:", err);
        setCountries(DEFAULT_COUNTRIES);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCountries(countries);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = countries.filter((country) =>
        country.label.toLowerCase().includes(query)
      );
      setFilteredCountries(filtered);
    }
  }, [searchQuery, countries]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else if (!isOpen) {
      setSearchQuery(""); 
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const selectedCountry = countries.find((c) => c.value === value) || countries[0];

  return (
    <div className="country-selector" ref={dropdownRef}>
      <button
        className="country-selector__button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="country-selector__label">Country:</span>
        <span className="country-selector__value">{selectedCountry.label}</span>
        <span className={`country-selector__arrow ${isOpen ? "country-selector__arrow--open" : ""}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="country-selector__dropdown" role="listbox">
          {/* Search Input */}
          <div className="country-selector__search">
            <input
              ref={searchInputRef}
              type="text"
              className="country-selector__search-input"
              placeholder="Type to search countries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setIsOpen(false);
                }
              }}
            />
          </div>
          
          {/* Countries List */}
          <ul className="country-selector__list">
            {loading ? (
              <li className="country-selector__option">Loading countries...</li>
            ) : filteredCountries.length === 0 ? (
              <li className="country-selector__option country-selector__option--no-results">
                No countries found
              </li>
            ) : (
              filteredCountries.map((country) => (
                <li
                  key={country.value}
                  className={`country-selector__option ${
                    value === country.value ? "country-selector__option--selected" : ""
                  }`}
                  role="option"
                  aria-selected={value === country.value}
                  onClick={() => {
                    // Pass both value and label for frontend filtering
                    onChange({
                      value: country.value === "all" ? "all" : country.value,
                      label: country.label
                    });
                    setIsOpen(false);
                    setSearchQuery("");
                  }}
                >
                  {country.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

